// src/components/engine/SystemStateIndicator.jsx
// §4.2 System State Indicator
//
// Четыре состояния:
//   Protected          — риска нет, система работает нормально
//   Observing          — confidence недостаточна, наблюдение
//   Action Recommended — medium risk, alert послан
//   Auto-Protected     — reduce/pause выполнен автоматически

import { useEffect, useState } from "react";
import { workerClient }        from "../../api/workerClient";

const STATE_CONFIG = {
  "Protected": {
    color: "#12cc76",
    dot:   "#12cc76",
    icon:  "✓",
    desc:  "No risk detected. Budget protection active.",
  },
  "Observing": {
    color: "#94a3b8",
    dot:   "#94a3b8",
    icon:  "◎",
    desc:  "VIRAJ detected signals but confidence is insufficient for action. System is observing before intervention.",
  },
  "Action Recommended": {
    color: "#f5a623",
    dot:   "#f5a623",
    icon:  "⚠",
    desc:  "Risk signal detected. Review recommended.",
  },
  "Auto-Protected": {
    color: "#e85555",
    dot:   "#12cc76",
    icon:  "⚡",
    desc:  "Automatic anti-loss action executed.",
  },
};

/**
 * @param {{ campaignId?: string }} props
 */
export default function SystemStateIndicator({ campaignId }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    const path = "/api/engine/status";
    workerClient(path)
      .then(res => {
        if (res.ok) setCampaigns(res.campaigns ?? []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    const interval = setInterval(() => {
      workerClient(path)
        .then(res => { if (res.ok) setCampaigns(res.campaigns ?? []); })
        .catch(() => {});
    }, 30_000);

    return () => clearInterval(interval);
  }, []);

  // Фильтрация по кампании
  const toShow = campaignId
    ? campaigns.filter(c => c.campaign_id === campaignId)
    : campaigns;

  if (loading) {
    return <div className="skeleton" style={{ height: 56, borderRadius: "var(--radius)" }} />;
  }

  if (!toShow.length) {
    return (
      <div style={{
        padding:    "14px 18px", borderRadius: "var(--radius)",
        background: "var(--bg-card)", border: "1px solid var(--border-subtle)",
        fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)",
      }}>
        No campaigns tracked yet
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {toShow.map(c => {
        const state  = c.system_state ?? "Observing";
        const config = STATE_CONFIG[state] ?? STATE_CONFIG["Observing"];

        return (
          <div key={c.campaign_id} style={{
            padding:      "12px 18px",
            borderRadius: "var(--radius)",
            background:   "var(--bg-card)",
            border:       `1px solid var(--border-subtle)`,
            display:      "flex", alignItems: "center", gap: 14,
          }}>
            {/* State dot */}
            <div style={{
              width:        8, height: 8, borderRadius: "50%",
              background:   config.dot,
              boxShadow:    `0 0 8px ${config.dot}80`,
              flexShrink:   0,
            }} />

            {/* Campaign ID */}
            <div style={{
              fontFamily:  "var(--font-mono)", fontSize: 11,
              color:       "var(--text-muted)", minWidth: 80,
            }}>
              {c.campaign_id}
            </div>

            {/* State label */}
            <div style={{
              fontFamily:  "var(--font-mono)", fontSize: 12,
              fontWeight:  700, color: config.color, flex: 1,
            }}>
              {config.icon} {state}
            </div>

            {/* Risk status */}
            <div style={{
              fontFamily:    "var(--font-mono)", fontSize: 9,
              letterSpacing: "0.12em", textTransform: "uppercase",
              color:         "var(--text-muted)",
            }}>
              {c.risk_status}
            </div>

            {/* Mode */}
            <div style={{
              fontFamily:    "var(--font-mono)", fontSize: 9,
              color:         "var(--text-tertiary)",
              textTransform: "uppercase",
            }}>
              {c.mode}
            </div>
          </div>
        );
      })}
    </div>
  );
}
